import {
  isAllIncluded,
  referencePackage,
  savingsPercent,
  sortCatalog,
} from "@/utils/packageFeatures.js";

/**
 * Lo que la portada dice de los paquetes —«desde el Paquete 30 no caducan»,
 * «$18 por folio», la calculadora— se deduce del catálogo, igual que las
 * tarjetas. Si el master cambia un precio o una bandera, la portada sigue
 * diciendo la verdad sin desplegar código.
 */

/** "10 o 20", "40 + 10 y 100 PRO": los nombres cortos en una frase. */
export const shortNames = (packages, type = "conjunction", locale = "es-MX") =>
  new Intl.ListFormat(locale, { style: "long", type }).format(
    packages.map((pkg) => pkg.shortName ?? pkg.name),
  );

/**
 * Los paquetes que la portada nombra. Cualquiera puede faltar si el catálogo
 * cambia; quien los usa tiene que aguantar el null.
 */
export const landingFacts = (packages) => {
  const catalog = sortCatalog(packages);
  const bestPerFolio = catalog.reduce(
    (best, pkg) =>
      !best || pkg.pricePerFolioCents < best.pricePerFolioCents ? pkg : best,
    null,
  );
  // En empate gana el más grande: la franja ya nombra al primero que llega al
  // mejor costo por folio, y así cada cifra habla de un paquete distinto.
  const topSavings = catalog.reduce(
    (top, pkg) =>
      !top || savingsPercent(pkg, catalog) >= savingsPercent(top, catalog)
        ? pkg
        : top,
    null,
  );

  return {
    catalog,
    reference: referencePackage(catalog),
    limited: catalog.filter((pkg) => !pkg.unlimitedValidity),
    firstUnlimited: catalog.find((pkg) => pkg.unlimitedValidity) ?? null,
    firstWithAdvisories: catalog.find((pkg) => pkg.advisories > 0) ?? null,
    withHistory: catalog.filter((pkg) => pkg.history),
    allIncluded: catalog.find(isAllIncluded) ?? null,
    bestPerFolio,
    topSavings,
    topSavingsPercent: topSavings ? savingsPercent(topSavings, catalog) : 0,
  };
};

/** Tope de la barra de la calculadora: el paquete que más folios acredita. */
export const maxFolios = (packages) =>
  Math.max(0, ...(packages ?? []).map((pkg) => pkg.creditedFolios));

/**
 * Calculadora: el paquete más barato que acredita al menos `need` folios. Si
 * ninguno alcanza, el que más acredita.
 */
export const recommendPackage = (packages, need) => {
  const catalog = sortCatalog(packages);
  return (
    catalog.find((pkg) => pkg.creditedFolios >= need) ??
    catalog.reduce(
      (big, pkg) =>
        !big || pkg.creditedFolios > big.creditedFolios ? pkg : big,
      null,
    )
  );
};

/**
 * Cuánto se ahorra frente a comprar esos mismos folios al costo por folio del
 * paquete de referencia (el más caro por folio), en centavos.
 */
export const savingsCents = (pkg, packages) => {
  const base = referencePackage(sortCatalog(packages))?.pricePerFolioCents ?? 0;
  return Math.max(0, pkg.creditedFolios * base - pkg.priceCents);
};
